"use client";

/**
 * ButtonGuide — what clicking the askFinz button on OUR page does.
 *
 * A web page cannot add a bookmark itself: no current browser lets a page do it (only an installed
 * extension can). So the button has to be dragged onto the bookmarks bar, and a click is the moment
 * a visitor tells us they did not know that. This dialog shows how, for the browser they are on:
 *   - a short looping animation of the button being dropped onto a bookmarks bar;
 *   - the keys that show the bar (Ctrl+Shift+B, or Cmd+Shift+B on a Mac);
 *   - Firefox: right-click the button and choose "Bookmark Link";
 *   - Safari on iPhone/iPad and other phones, where dragging is not possible: copy the button's code,
 *     bookmark any page, edit that bookmark and paste the code as its address.
 *
 * The platform is read when the dialog opens (an event, never during render), so server and client
 * render the same HTML. Motion is switched off for visitors who ask for reduced motion.
 */

import { useEffect, useRef, useState } from "react";
import { Check, Copy, MousePointerClick, X } from "lucide-react";

type Platform = { mac: boolean; firefox: boolean; touch: boolean; safari: boolean };

export function detectPlatform(): Platform {
  const ua = navigator.userAgent;
  const mac = /Mac|iPhone|iPad|iPod/.test(navigator.platform || ua);
  const touch = /iPhone|iPad|iPod|Android/i.test(ua) || (mac && navigator.maxTouchPoints > 1);
  const firefox = /Firefox\//.test(ua);
  const safari = /Safari\//.test(ua) && !/Chrome\/|Chromium\/|Edg\//.test(ua);
  return { mac, firefox, touch, safari };
}

function Keys({ keys }: { keys: string[] }) {
  return (
    <span className="inline-flex items-center gap-1 align-middle">
      {keys.map((k, i) => (
        <span key={i} className="inline-flex items-center gap-1">
          {i > 0 && <span className="text-foreground/40">+</span>}
          <kbd className="rounded-md border hairline-strong bg-background px-1.5 py-0.5 font-mono text-[11px] text-foreground shadow-[0_1px_0_hsl(var(--foreground)/0.15)]">
            {k}
          </kbd>
        </span>
      ))}
    </span>
  );
}

/** The bookmarks bar, with the button travelling up into it and settling. Pure CSS. */
function DragAnimation() {
  return (
    <div aria-hidden className="relative h-36 overflow-hidden rounded-xl border hairline bg-background">
      <style>{`
        @keyframes askfinz-drag {
          0%, 12%   { transform: translate(-50%, 78px) scale(1); opacity: 1; }
          45%       { transform: translate(-50%, 10px) scale(0.92); opacity: 1; }
          58%, 82%  { transform: translate(-50%, 10px) scale(0.92); opacity: 0; }
          100%      { transform: translate(-50%, 78px) scale(1); opacity: 0; }
        }
        @keyframes askfinz-drop {
          0%, 44%   { opacity: 0; transform: scale(0.8); }
          52%, 88%  { opacity: 1; transform: scale(1); }
          100%      { opacity: 0; transform: scale(1); }
        }
        @keyframes askfinz-cursor {
          0%, 12%   { transform: translate(18px, 92px); }
          45%       { transform: translate(18px, 24px); }
          58%, 100% { transform: translate(18px, 24px); opacity: 0; }
        }
        .askfinz-drag   { animation: askfinz-drag 3.2s ease-in-out infinite; }
        .askfinz-drop   { animation: askfinz-drop 3.2s ease-in-out infinite; }
        .askfinz-cursor { animation: askfinz-cursor 3.2s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .askfinz-drag, .askfinz-cursor { animation: none; opacity: 0; }
          .askfinz-drop { animation: none; opacity: 1; }
        }
      `}</style>
      {/* Browser chrome: the bookmarks bar */}
      <div className="flex h-9 items-center gap-2 border-b hairline bg-foreground/[0.04] px-3">
        <span className="h-2.5 w-14 rounded-full bg-foreground/15" />
        <span className="h-2.5 w-10 rounded-full bg-foreground/15" />
        <span className="askfinz-drop ml-1 inline-flex items-center gap-1 rounded-md bg-[hsl(var(--accent))] px-2 py-0.5 text-[10.5px] font-medium text-background">
          <MousePointerClick className="h-3 w-3" />
          askFinz Data Share
        </span>
        <span className="ml-auto text-[10px] font-mono uppercase tracking-[0.14em] text-foreground/40">bookmarks bar</span>
      </div>
      {/* The page, with the button being dragged up */}
      <div className="px-4 pt-3">
        <span className="block h-2 w-40 rounded-full bg-foreground/10" />
        <span className="mt-2 block h-2 w-28 rounded-full bg-foreground/10" />
      </div>
      <span className="askfinz-drag absolute left-1/2 top-0 inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--accent))] px-3 py-1.5 text-[12px] font-medium text-background shadow-lg">
        <MousePointerClick className="h-3.5 w-3.5" />
        askFinz Data Share
      </span>
      <svg className="askfinz-cursor absolute left-1/2 top-0 h-5 w-5 text-foreground drop-shadow" viewBox="0 0 24 24" fill="currentColor">
        <path d="M5 3l14 8-6.5 1.5L9.5 19z" />
      </svg>
    </div>
  );
}

export function ButtonGuide({ open, onClose, code }: { open: boolean; onClose: () => void; code: string }) {
  const [platform, setPlatform] = useState<Platform | null>(null);
  const [copied, setCopied] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      setPlatform(detectPlatform());
      setCopied(false);
      closeRef.current?.focus();
    }, 0);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;
  const p = platform ?? { mac: false, firefox: false, touch: false, safari: false };
  const mod = p.mac ? "⌘ Cmd" : "Ctrl";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="askfinz-guide-title"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-2xl border hairline-strong bg-card p-5 shadow-2xl"
      >
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 rounded-full p-1.5 text-foreground/55 hover:bg-foreground/5 hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="kicker">The askFinz button</div>
        <h3 id="askfinz-guide-title" className="mt-2 display-serif text-xl leading-snug">
          {p.touch ? "Add it as a bookmark first" : "Drag me to your bookmarks bar"}
        </h3>
        <p className="mt-2 text-[13px] leading-relaxed text-foreground/70">
          Browsers don&#39;t let a website add a bookmark for you, so this button only works once it is in your bookmarks.
          It takes a few seconds, once.
        </p>

        {!p.touch && (
          <>
            <div className="mt-4">
              <DragAnimation />
            </div>
            <ol className="mt-4 space-y-2.5 text-[13px] leading-relaxed text-foreground/80">
              <li className="flex gap-2.5">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border hairline-strong font-mono text-[10px]">1</span>
                <span>
                  Show your bookmarks bar: press <Keys keys={[mod, "Shift", "B"]} />.
                </span>
              </li>
              <li className="flex gap-2.5">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border hairline-strong font-mono text-[10px]">2</span>
                <span>Close this, then drag the red askFinz Data Share button up onto the bar.</span>
              </li>
              <li className="flex gap-2.5">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border hairline-strong font-mono text-[10px]">3</span>
                <span>Open a shared chat from any assistant, then click the button in your bar.</span>
              </li>
            </ol>
            {p.firefox && (
              <p className="mt-3 rounded-lg border hairline bg-background/60 px-3 py-2 text-[12px] text-foreground/70">
                In Firefox you can also right-click the button and choose <span className="font-medium text-foreground">Bookmark Link</span>.
              </p>
            )}
          </>
        )}

        {p.touch && (
          <ol className="mt-4 space-y-2.5 text-[13px] leading-relaxed text-foreground/80">
            <li className="flex gap-2.5">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border hairline-strong font-mono text-[10px]">1</span>
              <span>Copy the button&#39;s code below.</span>
            </li>
            <li className="flex gap-2.5">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border hairline-strong font-mono text-[10px]">2</span>
              <span>Bookmark this page, then edit that bookmark: name it &quot;askFinz Data Share&quot; and paste the code as its address.</span>
            </li>
            <li className="flex gap-2.5">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border hairline-strong font-mono text-[10px]">3</span>
              <span>Open a shared chat, then open that bookmark.</span>
            </li>
          </ol>
        )}

        <div className="mt-5 flex flex-wrap items-center gap-2 border-t hairline pt-4">
          <button
            type="button"
            onClick={copy}
            disabled={!code}
            className="inline-flex items-center gap-1.5 rounded-full border hairline-strong px-3.5 py-1.5 text-[12.5px] font-medium hover:bg-foreground/5 disabled:opacity-40"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Code copied" : "Copy button code"}
          </button>
          <span className="text-[11.5px] text-foreground/50">
            {p.touch ? "Needed on phones and tablets." : "For browsers where dragging doesn't work."}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto inline-flex items-center rounded-full bg-foreground px-4 py-1.5 text-[12.5px] font-medium text-background hover:opacity-90"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
